import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { RefreshCw, CheckCircle, X, Search, Send } from "lucide-react";

const statusConfig = {
  pending_review: { label: "À traiter",  color: "bg-amber-100 text-amber-700" },
  relanced:       { label: "Relancé",    color: "bg-blue-100 text-blue-700" },
  converted:      { label: "Converti",   color: "bg-green-100 text-green-700" },
  ignored:        { label: "Ignoré",     color: "bg-gray-100 text-gray-500" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "—";
  const diff = Math.floor((new Date() - new Date(dateStr)) / 60000);
  if (diff < 1) return "À l'instant";
  if (diff < 60) return `Il y a ${diff}min`;
  if (diff < 1440) return `Il y a ${Math.floor(diff / 60)}h`;
  return `Il y a ${Math.floor(diff / 1440)}j`;
}

function RelanceDrawer({ item, onClose, onUpdate }) {
  const [message, setMessage] = useState(
    `Bonjour ! 👋 On a vu que vous nous avez contactés récemment. Avez-vous encore besoin d'aide ? Nous sommes là pour vous 😊`
  );
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  async function ignore() {
    const { data } = await supabase
      .from('abandoned_conversations')
      .update({ status: 'ignored' })
      .eq('id', item.id)
      .select()
      .single();
    onUpdate(data);
    onClose();
  }

  async function sendRelance() {
    setSending(true);
    try {
      await fetch(`https://smart-crm-production.up.railway.app/api/abandoned/${item.id}/relance`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message })
      });
      const { data } = await supabase
        .from('abandoned_conversations')
        .update({ status: 'relanced', relance_sent_at: new Date(), relance_message: message })
        .eq('id', item.id)
        .select()
        .single();
      onUpdate(data);
      setSent(true);
      setTimeout(() => { setSent(false); onClose(); }, 1500);
    } catch (e) {
      console.error(e);
    } finally {
      setSending(false);
    }
  }

  const sta = statusConfig[item.status] || statusConfig.pending_review;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl" onClick={e => e.stopPropagation()}>

        <div className="sticky top-0 bg-white border-b border-gray-100 px-4 py-3 flex items-center justify-between z-10">
          <span className="text-sm font-medium text-gray-800">Relancer ce contact</span>
          <button onClick={onClose} className="p-1 rounded-lg hover:bg-gray-100">
            <X size={16} className="text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-5">

          {/* Infos contact */}
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-800">{item.phone}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sta.color}`}>
                {sta.label}
              </span>
            </div>
            <div className="text-xs text-gray-500">
              Dernier message : <span className="text-gray-700">"{item.last_message}"</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-400">
              <span>⏱ {timeAgo(item.last_message_at)}</span>
              <span className="capitalize">📡 {item.channel}</span>
            </div>
          </div>

          {/* Message de relance */}
          <div>
            <div className="text-xs font-medium text-gray-700 mb-1.5">
              Message de relance
              <span className="text-gray-400 font-normal ml-1">(personnalisable)</span>
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows={5}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400 resize-none"
            />
            <div className="text-xs text-gray-400 mt-1">
              Ce message sera envoyé sur {item.channel}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={ignore}
              className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm rounded-xl hover:bg-gray-50"
            >
              Ignorer
            </button>
            <button
              onClick={sendRelance}
              disabled={sending || !message.trim()}
              className="flex-1 py-2.5 bg-orange-600 text-white text-sm rounded-xl hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {sent ? (
                <><CheckCircle size={14} /> Envoyé !</>
              ) : sending ? (
                'Envoi...'
              ) : (
                <><Send size={14} /> Relancer</>
              )}
            </button>
          </div>

          {/* Historique si déjà relancé */}
          {item.relance_sent_at && (
            <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
              <div className="text-xs font-medium text-blue-700 mb-1">Déjà relancé</div>
              <div className="text-xs text-blue-600">
                {timeAgo(item.relance_sent_at)} — "{item.relance_message}"
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Relances() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending_review');
  const [selected, setSelected] = useState(null);
  const [orgPlan, setOrgPlan] = useState('starter');

  useEffect(() => {
    async function init() {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id, plan_name, subscription_plan, is_trial')
        .eq('user_id', user.id)
        .single();
      if (orgData?.id) {
        setOrgPlan(orgData.is_trial ? 'trial' : (orgData.plan_name || orgData.subscription_plan || 'starter'));
        await loadItems(orgData.id);
      }
      setLoading(false);
    }
    init();
  }, []);

  async function loadItems(id) {
    try {
      const { data } = await supabase
        .from('abandoned_conversations')
        .select('*')
        .eq('org_id', id)
        .order('last_message_at', { ascending: false });
      setItems(data || []);
    } catch (e) {
      console.error(e);
    }
  }

  function handleUpdate(updated) {
    setItems(prev => prev.map(i => i.id === updated.id ? updated : i));
    setSelected(updated);
  }

  const canRelance = ['pro', 'premium'].includes(orgPlan);

  const filtered = items.filter(item => {
    const matchSearch = !search || (item.phone || '').includes(search);
    const matchStatus = filterStatus === 'all' || item.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const stats = {
    total:     items.length,
    pending:   items.filter(i => i.status === 'pending_review').length,
    relanced:  items.filter(i => i.status === 'relanced').length,
    converted: items.filter(i => i.status === 'converted').length,
  };

  // Bloquer si plan insuffisant
  if (!canRelance) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-4">
          <RefreshCw size={28} className="text-orange-500" />
        </div>
        <h2 className="text-base font-medium text-gray-800 mb-2">Relances automatiques</h2>
        <p className="text-sm text-gray-500 max-w-xs mb-4">
          Hellodia détecte les conversations abandonnées et vous permet de relancer automatiquement vos prospects.
        </p>
        <div className="bg-orange-50 border border-orange-200 rounded-xl px-5 py-3 text-sm text-orange-700 font-medium">
          Disponible dès le plan Pro — 119€/mois
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Total",     value: stats.total,     color: "text-gray-800" },
          { label: "À traiter", value: stats.pending,   color: "text-amber-600" },
          { label: "Relancés",  value: stats.relanced,  color: "text-blue-600" },
          { label: "Convertis", value: stats.converted, color: "text-green-600" },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4">
            <div className="text-xs text-gray-500 mb-1">{s.label}</div>
            <div className={`text-xl font-medium ${s.color}`}>
              {loading ? "..." : s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Barre actions */}
      <div className="flex items-center gap-2">
        <div className="flex-1 relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher par numéro..."
            className="w-full pl-8 pr-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-orange-400"
          />
        </div>
      </div>

      {/* Filtres statut */}
      <div className="flex gap-2 flex-wrap">
        {[
          ['all', 'Tous'],
          ['pending_review', 'À traiter'],
          ['relanced', 'Relancés'],
          ['converted', 'Convertis'],
          ['ignored', 'Ignorés'],
        ].map(([val, label]) => (
          <button
            key={val}
            onClick={() => setFilterStatus(val)}
            className={`px-3 py-1.5 rounded-full text-xs border font-medium transition-colors ${
              filterStatus === val
                ? 'bg-orange-100 text-orange-700 border-orange-300'
                : 'border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            {label}
            {val === 'pending_review' && stats.pending > 0 && (
              <span className="ml-1.5 bg-amber-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {stats.pending}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <span className="text-sm font-medium text-gray-800">
            {filtered.length} conversation{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>

        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-gray-400">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="px-4 py-8 text-center">
            <div className="text-2xl mb-2">🔄</div>
            <div className="text-sm text-gray-500 font-medium">Aucune conversation abandonnée</div>
            <div className="text-xs text-gray-400 mt-1">
              Hellodia détecte automatiquement les conversations qui n'ont pas abouti
            </div>
          </div>
        ) : (
          filtered.map((item, i) => {
            const sta = statusConfig[item.status] || statusConfig.pending_review;
            return (
              <div
                key={i}
                onClick={() => setSelected(item)}
                className="px-4 py-3 border-b border-gray-50 last:border-0 hover:bg-gray-50 cursor-pointer"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-gray-800">{item.phone}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${sta.color}`}>
                        {sta.label}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      "{item.last_message}"
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                      <span>⏱ {timeAgo(item.last_message_at)}</span>
                      <span className="capitalize">📡 {item.channel}</span>
                      {item.relance_sent_at && (
                        <span className="text-blue-500">Relancé {timeAgo(item.relance_sent_at)}</span>
                      )}
                    </div>
                  </div>
                  {item.status === 'pending_review' && (
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-amber-500 rounded-full mt-2" />
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Drawer */}
      {selected && (
        <RelanceDrawer
          item={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
        />
      )}
    </div>
  );
}